import { useTranslation } from "@sagot/i18n";
import { Button } from "@sagot/ui";
import { ProfileFields } from "./profile-fields";
import { TemplateDetails } from "./template-details";
import { TemplatePicker } from "./template-picker";
import type { SetupChoices } from "./use-setup-choices";
import { useTemplatePreview } from "./use-template-preview";

interface TemplateSectionProps {
  choices: SetupChoices;
  country: string;
  /** Organization name typed in step 1 (fills the template copy's `{{org_name}}`). */
  orgName: string;
  /** The user already tried to continue: unanswered required questions show their error. */
  attempted: boolean;
}

/**
 * Step 1, third question: pick a template, see what it sets up (loaded from the server, skeleton
 * while loading) and answer the follow-up questions of that template.
 */
export function TemplateSection({ choices, country, orgName, attempted }: TemplateSectionProps) {
  const { t, i18n } = useTranslation();
  const ready = choices.industry !== null;
  const selected = ready ? choices.ranked.find((template) => template.key === choices.template) : undefined;
  const recommended = choices.ranked.find((template) => template.key === choices.recommended);
  const preview = useTemplatePreview(selected?.key, choices.goals, i18n.language);
  const differsFromRecommended = selected !== undefined && recommended !== undefined && selected.key !== recommended.key;
  return (
    <section aria-labelledby="templates-title" className="space-y-4" data-testid="template-section">
      <div>
        <h2 id="templates-title" className="text-base font-bold">
          {t("onboarding.org.templatesHeading")}
        </h2>
        <p className="text-sm text-fg-muted">{t("onboarding.org.templatesSubtitle")}</p>
      </div>
      <TemplatePicker
        templates={ready ? choices.ranked : choices.templates.data}
        isPending={choices.templates.isPending}
        isError={choices.templates.isError}
        value={choices.template}
        recommended={choices.recommended}
        disabled={!ready}
        onChange={choices.pickTemplate}
        onRetry={() => void choices.templates.refetch()}
      />
      {ready ? null : <p className="text-sm text-fg-muted">{t("onboarding.org.templatesNeedIndustry")}</p>}
      {differsFromRecommended ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted" data-testid="template-choice-hint">
          <p>{t("onboarding.org.templateChosen", { chosen: selected.name, recommended: recommended.name })}</p>
          <Button type="button" variant="ghost" size="sm" onClick={() => { choices.pickTemplate(recommended.key); }}>
            {t("onboarding.org.useRecommended")}
          </Button>
        </div>
      ) : null}
      {selected ? (
        <TemplateDetails
          template={selected}
          preview={preview.data}
          isPending={preview.isPending}
          isRefreshing={preview.isPlaceholderData}
          isError={preview.isError}
          isRecommended={!differsFromRecommended}
          orgName={orgName}
          onRetry={() => void preview.refetch()}
        />
      ) : null}
      {selected && choices.fields.length ? (
        <div className="card" data-testid="template-questions">
          <div className="card-body">
            <h3 className="text-base font-bold">{t("onboarding.org.details")}</h3>
            <p className="mb-3 text-sm text-fg-muted">{t("onboarding.org.detailsHint", { template: selected.name })}</p>
            <ProfileFields fields={choices.fields} answers={choices.answers} country={country} onChange={choices.setAnswers} missing={attempted ? choices.missing : []} />
          </div>
        </div>
      ) : null}
    </section>
  );
}
